<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Distribuicao de Carga por Canal',
  },
});

const balanceStatus = computed(() => props.data?.balance_status || 'unknown');
const inboxes = computed(() => props.data?.inboxes || []);
const totalConversations = computed(() => props.data?.total_conversations || 0);
const avgPerInbox = computed(() => props.data?.average_per_inbox || 0);
const busiest = computed(() => props.data?.busiest || null);
const quietest = computed(() => props.data?.quietest || null);

const maxConversations = computed(() => {
  if (inboxes.value.length === 0) return 1;
  return Math.max(...inboxes.value.map(i => i.conversations), 1);
});

function getBalanceInfo(status) {
  const info = {
    balanced: {
      bg: 'bg-n-teal-3',
      text: 'text-n-teal-11',
      icon: 'i-lucide-check-circle',
      label: 'Equilibrada',
    },
    slightly_unbalanced: {
      bg: 'bg-n-amber-3',
      text: 'text-n-amber-11',
      icon: 'i-lucide-alert-circle',
      label: 'Ligeiramente Desbalanceada',
    },
    unbalanced: {
      bg: 'bg-n-ruby-3',
      text: 'text-n-ruby-11',
      icon: 'i-lucide-alert-triangle',
      label: 'Desbalanceada',
    },
  };
  return (
    info[status] || {
      bg: 'bg-n-slate-3',
      text: 'text-n-slate-11',
      icon: 'i-lucide-help-circle',
      label: 'Desconhecido',
    }
  );
}

function getBarColor(percentage) {
  if (percentage <= 50) return 'bg-n-blue-9';
  if (percentage <= 120) return 'bg-n-teal-9';
  if (percentage <= 150) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function getChannelIcon(channelType) {
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-code',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-scale text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Balance Status -->
      <div
        class="flex items-center gap-4 p-4 rounded-lg"
        :class="getBalanceInfo(balanceStatus).bg"
      >
        <span
          class="text-2xl"
          :class="[
            getBalanceInfo(balanceStatus).icon,
            getBalanceInfo(balanceStatus).text,
          ]"
        />
        <div class="flex-1">
          <div
            class="text-sm font-medium"
            :class="getBalanceInfo(balanceStatus).text"
          >
            {{ getBalanceInfo(balanceStatus).label }}
          </div>
          <div class="text-xs text-n-slate-11 mt-0.5">
            Media: {{ avgPerInbox.toFixed(1) }} conv/canal | Desvio:
            {{ (data.standard_deviation || 0).toFixed(1) }}
          </div>
        </div>
        <div class="text-right">
          <div class="text-xl font-bold text-n-slate-12">
            {{ totalConversations }}
          </div>
          <div class="text-xs text-n-slate-11">total</div>
        </div>
      </div>

      <!-- Busiest and Quietest -->
      <div v-if="busiest || quietest" class="grid grid-cols-2 gap-3">
        <div
          v-if="busiest"
          class="p-3 rounded-lg bg-n-ruby-3/30 border border-n-ruby-5"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-flame text-sm text-n-ruby-10" />
            <span class="text-xs font-medium text-n-ruby-11">Mais Movimentado</span>
          </div>
          <div class="text-sm font-medium text-n-slate-12 truncate">
            {{ busiest.inbox_name }}
          </div>
          <div class="text-lg font-bold text-n-ruby-11">
            {{ busiest.conversations }} conv.
          </div>
        </div>
        <div
          v-if="quietest"
          class="p-3 rounded-lg bg-n-blue-3/30 border border-n-blue-5"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-moon text-sm text-n-blue-10" />
            <span class="text-xs font-medium text-n-blue-11">Mais Tranquilo</span>
          </div>
          <div class="text-sm font-medium text-n-slate-12 truncate">
            {{ quietest.inbox_name }}
          </div>
          <div class="text-lg font-bold text-n-blue-11">
            {{ quietest.conversations }} conv.
          </div>
        </div>
      </div>

      <!-- Inbox bars -->
      <div class="flex flex-col gap-2 max-h-48 overflow-y-auto">
        <div
          v-for="inbox in inboxes"
          :key="inbox.inbox_id"
          class="flex items-center gap-2"
        >
          <span
            :class="getChannelIcon(inbox.channel_type)"
            class="text-sm text-n-slate-11"
          />
          <span
            class="text-xs text-n-slate-11 w-24 truncate"
            :title="inbox.inbox_name"
          >
            {{ inbox.inbox_name }}
          </span>
          <div class="flex-1 h-4 bg-n-alpha-2 rounded overflow-hidden">
            <div
              class="h-full rounded transition-all duration-300"
              :class="getBarColor(inbox.vs_average)"
              :style="{
                width: `${(inbox.conversations / maxConversations) * 100}%`,
              }"
            />
          </div>
          <span class="text-xs font-medium text-n-slate-12 w-10 text-right">
            {{ inbox.conversations }}
          </span>
          <span class="text-xs text-n-slate-10 w-12 text-right">
            {{ inbox.percentage }}%
          </span>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex items-center justify-center gap-4 pt-3 border-t border-n-weak text-xs"
      >
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-blue-9" />
          <span class="text-n-slate-11">Baixa</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-teal-9" />
          <span class="text-n-slate-11">Normal</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-amber-9" />
          <span class="text-n-slate-11">Alta</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-ruby-9" />
          <span class="text-n-slate-11">Muito Alta</span>
        </div>
      </div>
    </div>
  </div>
</template>
